/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import { Fragment, useEffect, useMemo, useState, useContext } from 'react'; // Додано useContext
import {
  Col,
  Row,
  message,
  Radio,
  Select,
  Pagination,
  Button,
  Menu,
  Dropdown,
} from 'antd';
import {
  CopyOutlined,
  DownloadOutlined,
  DownOutlined,
} from '@ant-design/icons';
import { useQuestions } from '~src/hooks/api-hooks/useQuestions';
import { useQuestionsExport } from '~src/hooks/useQuestionsExport';
import { appLog } from '~src/utils/log';
import { useIntl } from 'react-intl';
import { formatDate } from '~src/utils/formatDate';
import { DB_STATE } from '~src/types/enums';
import { queryCache, useMutation, useQuery } from 'react-query';
import {
  addAnswerToQuestion,
  changeQuestionState,
} from '~src/services/api/questions';
import { QueryKeys } from '~src/hooks/api-hooks/queryKeys';
import QuestionsList, {
  PaginationParams,
} from '~components/atoms/Questions/QuestionsList';
import {
  LETTER_DEFAULT_PAGE_SIZE,
  QUESTIONS_DEFAULT_PAGE_SIZE,
} from '~src/config';
import { ThemedStyleFn, useStyles } from '~src/hooks/useStyles';
import QuestionsAnswerUpperBlock from '~components/molecules/Tabs/TenderTabs/components/Questions/QuestionsAnswerUpperBlock';
import QuestionsAnswerDateBlock from '~components/molecules/Tabs/TenderTabs/components/Questions/QuestionsAnswerDateBlock';
import { useRegistrationCoreTendersDetails } from '~src/hooks/api-hooks/useRegistrationCoreTendersDetails';

import TenderCorrectionLettersList from '~components/molecules/Tabs/TenderTabs/components/Questions/TenderCorrectionLettersList';
import { getAllMessages } from '~src/services/api/movements';
import { CorrelationLetterModal } from '~components/popups-modals/MovementsModals/CorrelationLetter.modal';
import { OffersMessagesModal } from '~components/popups-modals/OffersTabModals/OffersMessagesModal';
import {
  MESSAGE_TYPE_CONFIG,
  renderMessageTypeOption,
  ReferenceFilter,
} from '~src/utils/messageTypeUtils';
import { SearchContext } from '~src/contexts/SearchContext';
import { filterByQuery } from '~src/utils/searchFilters';
import IconButton from '~components/_common/IconButton/IconButton';
import { useDynamicHeightAndLockScroll } from '~src/hooks/useDynamicHeightAndLockScroll';
import CopyQAModal from '~components/popups-modals/CopyQAModal/CopyQAModal';

export type QuestionsBaseMetadata = {
  tenderId: string;
  projectId?: number;
};

type ChangesMode = 1 | 2; // 1 = all, 2 = latest
type ViewMode = 'qa' | 'letters';
type MessageTypeFilter = 'all' | '2' | '5' | '6';

const isLatestQuestion = (q: any): boolean => {
  return (
    q?.report_data?.is_ignore === false &&
    q?.report_data?.is_report_sent === false
  );
};

const TenderQuestionBlock: React.FC<QuestionsBaseMetadata> = ({
  tenderId,
  projectId,
}) => {
  const intl = useIntl();
  const styles = useStyles(styleFn);
  const { query } = useContext(SearchContext);

  const {
    containerRef,
    dynamicHeight,
  } = useDynamicHeightAndLockScroll<HTMLDivElement>();

  const [showRejected] = useState(true);

  const [changesMode, setChangesMode] = useState<ChangesMode>(1);
  const [messageTypeFilter, setMessageTypeFilter] = useState<MessageTypeFilter>(
    'all'
  );
  const isLatestMode = changesMode === 2;
  const [referenceFilter, setReferenceFilter] = useState<ReferenceFilter>(
    'ALL'
  );

  const [viewMode, setViewMode] = useState<ViewMode>('qa');
  const [selectedLetterForView, setSelectedLetterForView] = useState<any>(null);
  const [selectedEvalMsgForView, setSelectedEvalMsgForView] = useState<any>(
    null
  );
  const [copyQAModalVisible, setCopyQAModalVisible] = useState(false);

  // Pagination Q&A
  const [pagination, setPagination] = useState<PaginationParams>({
    current: 1,
    pageSize: QUESTIONS_DEFAULT_PAGE_SIZE,
    hasMore: false,
    total: 0,
  });

  // Pagination Messages/Letters
  const [
    messagesPagination,
    setMessagesPagination,
  ] = useState<PaginationParams>({
    current: 1,
    pageSize: LETTER_DEFAULT_PAGE_SIZE,
    hasMore: false,
    total: 0,
  });

  useEffect(() => {
    setPagination((prev) => ({ ...prev, current: 1 }));
    setMessagesPagination((prev) => ({ ...prev, current: 1 }));
  }, [query]);

  const tenderDetails = useRegistrationCoreTendersDetails({
    tenderId,
  });

  const {
    handleCopyToClipboard,
    handleDownloadCsv,
    handleCopyCsvInline,
  } = useQuestionsExport({ projectId, tenderId });

  const { data, isFetching } = useQuestions(
    {
      tenderId,
      projectId,
      skip: (pagination?.current - 1) * pagination.pageSize,
      take: pagination.pageSize,
      showRejected,
    },
    {
      onSuccess: (data) => {
        setPagination((prev) => ({
          ...prev,
          hasMore: data.questions.length === pagination.pageSize,
          total: data.totalCount,
        }));
      },
    }
  );

  const filteredQuestions = useMemo(() => {
    let qs = data?.questions || [];

    if (isLatestMode) {
      qs = qs.filter(isLatestQuestion);
    }

    if (query) {
      qs = qs.filter(
        (q: any) =>
          filterByQuery(q.question, query) ||
          (q.answers || []).some((a: any) => filterByQuery(a.answer, query))
      );
    }

    return qs;
  }, [data?.questions, isLatestMode, query]);

  const lettersQuery = useQuery(
    ['all_tender_messages', projectId, tenderId],
    () =>
      getAllMessages({
        projectId: Number(projectId),
        tenderId: Number(tenderId),
        sort: 'desc(sent_at)',
      }),
    {
      enabled: viewMode === 'letters' && !!projectId && !!tenderId,
    }
  );

  // Filters & Pagination for Letters list
  const filteredMessages = useMemo(() => {
    const allData = lettersQuery.data || [];

    return allData.filter((msg: any) => {
      const matchesType =
        messageTypeFilter === 'all' ||
        msg.type.toString() === messageTypeFilter;

      const matchesRef =
        referenceFilter === 'ALL' || msg.reference === referenceFilter;

      const matchesSearch =
        !query ||
        filterByQuery(msg.title, query) ||
        filterByQuery(msg.description, query);

      return matchesType && matchesRef && matchesSearch;
    });
  }, [lettersQuery.data, messageTypeFilter, referenceFilter, query]);

  const paginatedMessages = useMemo(() => {
    const start =
      (messagesPagination.current - 1) * messagesPagination.pageSize;
    return filteredMessages.slice(start, start + messagesPagination.pageSize);
  }, [
    filteredMessages,
    messagesPagination.current,
    messagesPagination.pageSize,
  ]);

  const messageCounts = useMemo(() => {
    const allData = lettersQuery.data || [];
    const counts: Record<ReferenceFilter, number> = {
      ALL: allData.filter((m) => m.type.toString() === '5').length,
      GEN: 0,
      INFO: 0,
      RESULT: 0,
      DEADLINE: 0,
      IMPORTANT: 0,
    };

    allData.forEach((m: any) => {
      if (
        m.type === 5 &&
        m.reference &&
        counts[m.reference as ReferenceFilter] !== undefined
      ) {
        counts[m.reference as ReferenceFilter]++;
      }
    });
    return counts;
  }, [lettersQuery.data]);

  useEffect(() => {
    setMessagesPagination((prev) => ({ ...prev, current: 1 }));
  }, [messageTypeFilter, referenceFilter]);

  const handlePaginationChange = (page: number, pageSize?: number) => {
    setPagination((prev) => ({
      ...prev,
      current: page,
      pageSize: pageSize || prev.pageSize,
    }));
  };

  const [addAnswerToQuestionMutation] = useMutation(addAnswerToQuestion, {
    onSuccess: () => {
      queryCache.invalidateQueries([
        QueryKeys.question,
        { projectId, tenderId },
      ]);
    },
    onError: (error, item) => {
      message.error(intl.formatMessage({ id: 'answer-question-failed' }));
      appLog('addAnswerToQuestionMutation -> item', item);
      appLog('addAnswerToQuestionMutation -> err', error);
    },
  });

  const [changeQuestionStateMutation] = useMutation(changeQuestionState, {
    onSuccess: () => {
      queryCache.invalidateQueries([
        QueryKeys.question,
        { projectId, tenderId },
      ]);
      queryCache.invalidateQueries(QueryKeys.questionsPendingCount);
    },
    onError: (error, item) => {
      message.error(intl.formatMessage({ id: 'failed' }));
      appLog('addAnswerToQuestionMutation -> item', item);
      appLog('addAnswerToQuestionMutation -> err', error);
    },
  });

  const handleExportClick = async (key: string) => {
    switch (key) {
      case 'copy-qa':
        setCopyQAModalVisible(true);
        break;
      case 'download-csv':
        await handleDownloadCsv();
        break;
      case 'copy-csv':
        await handleCopyCsvInline();
        break;
    }
  };

  const exportMenu = (
    <Menu onClick={(e) => handleExportClick(e.key as string)}>
      <Menu.Item key="copy-qa">
        <CopyOutlined rev={''} />{' '}
        {intl.formatMessage({ id: 'qa-copy-modal-title' })}
      </Menu.Item>
      <Menu.Item key="download-csv">
        <DownloadOutlined rev={''} />{' '}
        {intl.formatMessage({ id: 'qa-download-csv' })}
      </Menu.Item>
      <Menu.Item key="copy-csv">
        <CopyOutlined rev={''} />{' '}
        {intl.formatMessage({ id: 'qa-copy-csv-inline' })}
      </Menu.Item>
    </Menu>
  );

  const filtersHeader = (
    <div style={{ marginBottom: '20px' }}>
      <QuestionsAnswerUpperBlock />
      <div
        style={{
          marginTop: '14px',
          display: 'flex',
          alignItems: 'center',
          gap: 16,
          flexWrap: 'wrap',
        }}>
        {viewMode === 'qa' && (
          <Fragment>
            <Radio.Group
              value={changesMode}
              onChange={(e) => setChangesMode(e.target.value)}
              style={{ display: 'flex' }}>
              <Radio value={1} style={{ whiteSpace: 'nowrap' }}>
                {intl.formatMessage({ id: 'messages-tab-show-all-question' })}
              </Radio>
              <Radio value={2} style={{ whiteSpace: 'nowrap' }}>
                {intl.formatMessage({
                  id: 'messages-tab-correction-letter-awaiting',
                })}
              </Radio>
            </Radio.Group>

            <Dropdown overlay={exportMenu} trigger={['click']}>
              <Button
                disabled={isFetching || !data?.questions?.length}
                style={{ marginLeft: '8px' }}
                icon={<CopyOutlined rev={''} />}>
                {intl.formatMessage({ id: 'qa-export-options' })}{' '}
                <DownOutlined rev={''} />
              </Button>
            </Dropdown>
          </Fragment>
        )}

        {viewMode === 'letters' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Radio.Group
              value={messageTypeFilter}
              onChange={(e) => {
                setMessageTypeFilter(e.target.value);
                if (e.target.value === '2' || e.target.value === '6')
                  setReferenceFilter('ALL');
              }}>
              <Radio value="all">{intl.formatMessage({ id: 'all' })}</Radio>
              <Radio value="2">
                {intl.formatMessage({ id: 'correction-letters' })}
              </Radio>
              <Radio value="5">
                {intl.formatMessage({ id: 'instant-messages-filter' })}
              </Radio>
              <Radio value="6">
                {intl.formatMessage({
                  id: 'evaluation-instant-messages-filter',
                })}
              </Radio>
            </Radio.Group>

            {messageTypeFilter === '5' && (
              <Select
                value={referenceFilter}
                onChange={setReferenceFilter}
                style={{ width: 220 }}
                placeholder={intl.formatMessage({ id: 'filter-by-reference' })}>
                {MESSAGE_TYPE_CONFIG.map((config) => (
                  <Select.Option key={config.key} value={config.key}>
                    {renderMessageTypeOption(
                      config,
                      intl,
                      messageCounts[config.key]
                    )}
                  </Select.Option>
                ))}
              </Select>
            )}
          </div>
        )}

        <div style={{ marginLeft: 'auto' }}>
          <Radio.Group
            value={viewMode}
            onChange={(e) => setViewMode(e.target.value)}
            optionType="button"
            buttonStyle="solid"
            options={[
              {
                label: intl.formatMessage({ id: 'qa-option-questions' }),
                value: 'qa',
              },
              {
                label: (
                  <span
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                    }}>
                    <IconButton icon={['far', 'envelope']} backgroundOff />
                    {intl.formatMessage({ id: 'qa-option-letters' })}
                  </span>
                ),
                value: 'letters',
              },
            ]}
          />
        </div>
      </div>
    </div>
  );

  return (
    <Fragment css={styles.fragrentCss}>
      <div
        ref={containerRef}
        css={styles.mainWrapper}
        style={{ height: dynamicHeight }}>
        <Row gutter={[32, 24]} css={styles.contentRow} style={{ margin: 0 }}>
          <Col xs={24} lg={16} xl={18} css={styles.questionsColumn}>
            <div css={styles.questionsListContainer}>
              {filtersHeader}

              <div css={styles.listContentArea}>
                {viewMode === 'qa' && (
                  <Fragment>
                    <div css={styles.scrollableList}>
                      <QuestionsList
                        isLoading={isFetching}
                        handlePaginationChange={handlePaginationChange}
                        pagination={pagination}
                        questions={filteredQuestions}
                        split={false}
                        hideInternalPagination={true}
                        onAnswer={async (data, questionId) =>
                          addAnswerToQuestionMutation({
                            projectId,
                            tenderId,
                            questionId,
                            data: { answer: data.title },
                          })
                        }
                        onAccept={async (questionId) =>
                          changeQuestionStateMutation({
                            projectId,
                            tenderId,
                            questionId,
                            action: 'accept',
                          })
                        }
                        onReject={async (questionId) =>
                          changeQuestionStateMutation({
                            projectId,
                            tenderId,
                            questionId,
                            action: 'reject',
                          })
                        }
                        onIgnore={async (questionId) =>
                          changeQuestionStateMutation({
                            projectId,
                            tenderId,
                            questionId,
                            action: 'set_ignore_on',
                          })
                        }
                        onReactivate={async (questionId) =>
                          changeQuestionStateMutation({
                            projectId,
                            tenderId,
                            questionId,
                            action: 'set_ignore_off',
                          })
                        }
                        questionItemConfig={{
                          withQuestionAuthor: true,
                          withAnswerAuthor: true,
                          withStatus: true,
                          changeLogData: { projectId: projectId!, tenderId },
                        }}
                        isOnTender={false}
                      />
                    </div>
                    {/* Pagination QA */}
                    {!isFetching && filteredQuestions.length > 0 && (
                      <div css={styles.paginationFooter}>
                        <Pagination
                          current={pagination.current}
                          pageSize={pagination.pageSize}
                          total={pagination.total}
                          showSizeChanger={true}
                          pageSizeOptions={['5', '10', '20', '50']}
                          onShowSizeChange={(_, size) =>
                            handlePaginationChange(1, size)
                          }
                          onChange={(page, pageSize) =>
                            handlePaginationChange(page, pageSize)
                          }
                        />
                      </div>
                    )}
                  </Fragment>
                )}

                {viewMode === 'letters' && (
                  <Fragment>
                    <div css={styles.scrollableList}>
                      <TenderCorrectionLettersList
                        projectId={Number(projectId)}
                        tenderId={tenderId}
                        letters={paginatedMessages}
                        isLoading={lettersQuery.isFetching}
                        onViewLetter={(letter) =>
                          letter.type === 6
                            ? setSelectedEvalMsgForView(letter)
                            : setSelectedLetterForView(letter)
                        }
                      />
                    </div>

                    {!lettersQuery.isFetching && filteredMessages.length > 0 && (
                      <div css={styles.paginationFooter}>
                        <Pagination
                          current={messagesPagination.current}
                          pageSize={messagesPagination.pageSize}
                          total={filteredMessages.length}
                          showSizeChanger={true}
                          pageSizeOptions={['5', '10', '20', '50']}
                          onShowSizeChange={(_, size) =>
                            setMessagesPagination((prev) => ({
                              ...prev,
                              current: 1,
                              pageSize: size,
                            }))
                          }
                          onChange={(page, pageSize) =>
                            setMessagesPagination((prev) => ({
                              ...prev,
                              current: page,
                              pageSize: pageSize || prev.pageSize,
                            }))
                          }
                        />
                      </div>
                    )}
                  </Fragment>
                )}
              </div>
            </div>
          </Col>

          <Col xs={24} lg={8} xl={6} css={styles.dateColumn}>
            <div css={styles.dateBlockWrapper}>
              <QuestionsAnswerDateBlock
                qaPeriodStart={tenderDetails?.qa_start}
                qaPeriodEnd={tenderDetails?.qa_stop}
                qaOn={tenderDetails?.qa_on || false}
                qaActiveFrom={tenderDetails?.active_from}
                qaDeadlineAt={tenderDetails?.deadline_at}
                registrationState={tenderDetails?.state}
                tenderData={tenderDetails || null}
                projectId={projectId}
                tenderId={tenderId}
              />
            </div>
          </Col>
        </Row>
      </div>

      <CorrelationLetterModal
        visible={!!selectedLetterForView}
        onCancel={() => setSelectedLetterForView(null)}
        projectId={Number(projectId)}
        tenderId={Number(tenderId)}
        readOnly={true}
        readOnlyData={selectedLetterForView}
      />

      <OffersMessagesModal
        visible={!!selectedEvalMsgForView}
        onCancel={() => setSelectedEvalMsgForView(null)}
        projectId={Number(projectId)}
        tenderId={Number(tenderId)}
        readOnly={true}
        readOnlyData={selectedEvalMsgForView}
      />

      <CopyQAModal
        visible={copyQAModalVisible}
        onCancel={() => setCopyQAModalVisible(false)}
        questions={data?.questions || []}
        isVendorSide={false}
        projectId={projectId}
        tenderId={tenderId}
      />
    </Fragment>
  );
};

export default TenderQuestionBlock;

export const styleFn: ThemedStyleFn = (theme) => ({
  fragrentCss: {},
  mainWrapper: {
    width: '100%',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    minHeight: 0,
    boxSizing: 'border-box',
  },
  contentRow: {
    flex: 1,
    height: '100%',
    minHeight: 0,
  },
  questionsColumn: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: 0,
  },
  dateColumn: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: 0,
  },
  dateBlockWrapper: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    height: '100%',
    minHeight: 0,
    overflowY: 'auto',
    justifyContent: 'flex-start',
    gap: '25px',
    '@media (min-width: 992px)': { padding: '50px 0px 0px 0px' },
    '@media (max-width: 991px)': { padding: '0px', marginTop: '20px' },
  },
  questionsListContainer: {
    maxWidth: '900px',
    width: '100%',
    height: '100%',
    minHeight: 0,
    paddingTop: '30px',
    paddingLeft: '20px',
    minWidth: '75%',
    display: 'flex',
    flexDirection: 'column',
    boxSizing: 'border-box',
    '@media (max-width: 990px)': { minWidth: '100%' },
  },
  listContentArea: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
  },
  scrollableList: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    paddingRight: '6px',
    marginBottom: '10px',
    '&::-webkit-scrollbar': { width: '6px' },
    '&::-webkit-scrollbar-thumb': {
      background: '#d9d9d9',
      borderRadius: '10px',
    },
  },
  paginationFooter: {
    flex: '0 0 auto',
    display: 'flex',
    justifyContent: 'center',
    padding: '10px 0',
    borderTop: '1px solid #f0f0f0',
  },
});
